/**
 * The last line of defence.
 *
 * Without one of these, a single thrown error anywhere in the tree causes
 * React to unmount everything — and the reader gets a blank screen with no
 * message, no way back, and nothing to report. On a phone, where there is no
 * console to open, that is close to undiagnosable.
 *
 * Two failures it is built for, because they behave differently:
 *
 *   • A genuine bug in a component. Nothing to do but say so and offer a way
 *     out, with the message visible so it can be reported.
 *
 *   • A lazily-loaded route that failed to fetch. Every screen here is
 *     code-split, so a flaky connection — or a chunk that disappeared under a
 *     redeploy — throws on import rather than on render. That one is almost
 *     always fixed by reloading, so it says so and reloads properly, bypassing
 *     the cache.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface Props {
  children: ReactNode;
  /** Resets the boundary when it changes — usually the current route. */
  resetKey?: string;
}

interface State {
  error: Error | null;
}

const RELOAD_GUARD = 'ss.chunkReloaded';

/**
 * A failed dynamic import, rather than a broken component.
 *
 * Every browser words this differently, and one of them does not mention
 * modules at all:
 *
 *   Chrome   "Failed to fetch dynamically imported module: …"
 *   Firefox  "error loading dynamically imported module"
 *   Safari   "Importing a module script failed."
 *   Safari   "'text/html' is not a valid JavaScript MIME type."
 *
 * That last one is the important case here and the one this originally missed.
 * Firebase Hosting rewrites every unmatched path to index.html for the SPA
 * router, so a chunk filename that no longer exists after a deploy does not
 * 404 — it returns the HTML shell, and the browser complains about the MIME
 * type rather than about a missing module. Same underlying cause, completely
 * different words, so the match has to cover both.
 */
function isChunkError(error: Error): boolean {
  const message = error?.message ?? '';
  return (
    /dynamic|importing a module|module script/i.test(message) ||
    /mime type/i.test(message) ||
    /failed to (?:fetch|load).*(?:module|chunk|script)/i.test(message)
  );
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidUpdate(previous: Props): void {
    // Navigating away from a broken screen should clear the error rather than
    // trapping the reader on it.
    if (this.state.error && previous.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Kept in the console for a desktop debugging session; the interface below
    // is what a phone gets.
    console.error('[samuelschool] render failed:', error, info.componentStack);

    /*
     * Recover from a stale build automatically, once.
     *
     * Every screen is code-split, and a deploy replaces the hashed chunks. A
     * page that has been open — or an installed app holding an older
     * index.html — still points at chunk names that no longer exist, so the
     * first navigation to a screen it has not already loaded throws on import
     * and renders nothing. The reader did nothing wrong and has no idea a
     * deploy happened.
     *
     * Reloading fetches the current index.html and the problem evaporates. It
     * is guarded by a session flag so a genuinely broken build cannot put the
     * app into a reload loop — the second time, the message is shown instead.
     */
    if (!isChunkError(error)) return;
    try {
      if (sessionStorage.getItem(RELOAD_GUARD)) return;
      sessionStorage.setItem(RELOAD_GUARD, '1');
    } catch {
      return; // No storage means no guard, and a loop is worse than a message.
    }
    window.location.reload();
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    const chunkFailed = isChunkError(error);

    return (
      <main className="page">
        <div className="container container--reading">
          <div className="state">
            <div className="state-icon" aria-hidden="true" style={{ color: 'var(--danger)' }}>
              <AlertTriangle size={24} />
            </div>

            <p className="state-title">
              {chunkFailed ? "This screen didn't finish loading" : 'Something went wrong'}
            </p>

            <p className="state-body">
              {chunkFailed
                ? 'Part of the app failed to download. Reloading usually fixes it.'
                : 'This screen hit an error and stopped. Reloading will get you back.'}
            </p>

            <button
              type="button"
              className="btn btn--primary"
              // A plain reload can be served the same broken cache entry, so
              // this goes back to the entry point instead.
              onClick={() => window.location.replace('/')}
            >
              <RefreshCw size={16} />
              Reload SamuelSchool
            </button>

            {/*
              The message itself. Ugly, and worth it: it is the only thing that
              makes a bug report from a phone actionable.
            */}
            <p className="faint t-xs" style={{ maxWidth: '40ch', overflowWrap: 'anywhere' }}>
              {error.message || String(error)}
            </p>
          </div>
        </div>
      </main>
    );
  }
}
